/** @module One review case: your photo beside the suggestions, the details, and one decision. */
import { useQuery } from "@tanstack/react-query";
import { Check, Crop, Maximize2, Search, ShieldCheck, X } from "lucide-react";
import { useTranslation } from "react-i18next";

import { api, ApiError, problemFrom } from "@/api/client";
import { CardFace } from "@/components/card-face";
import { CardPicker, startingQuery } from "@/components/card-picker";
import { CompareDialog } from "@/features/review/compare-dialog";
import { CornerEditor } from "@/features/review/corner-editor";
import { NumberWarning } from "@/features/review/number-warning";
import { ResolveForm } from "@/components/resolve-form";
import { SwipeDeck } from "@/features/review/swipe-deck";
import {
  type Approval,
  type Candidate,
  ocrBacks,
  type Outcome,
  type ReviewModel,
  type Scan,
  useReviewCase,
} from "@/features/review/use-review-case";
import { type Card, printedAs, printedNumber, readNumber, useCard } from "@/lib/cards";

export { isTyping } from "@/features/review/use-review-case";

export type ReviewMode = "compare" | "swipe";

async function fetchReprints(printId: string): Promise<Card[]> {
  const { data, response } = await api.GET("/api/v1/cards/{print_id}/reprints", {
    params: { path: { print_id: printId }, query: { limit: 24 } },
  });
  if (!data) throw problemFrom(response);
  return data;
}

/** "0,86": the image similarity the server measured, as the design shows it. */
export function scoreText(score: number, locale: string): string {
  return score.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function Photo({ model, className = "" }: { model: ReviewModel; className?: string }) {
  const { t } = useTranslation();
  return model.photo ? (
    <img
      src={model.photo}
      alt={t("review.yourPhoto")}
      className={`card-shape w-full object-cover saturate-[.85] ${className}`}
    />
  ) : (
    <span className={`card-shape block w-full bg-(--surface-2) ${className}`} />
  );
}

/** A row of the phone's candidate list: thumb, name, number, a bar for the score. */
function CandidateRow({ model, candidate }: { model: ReviewModel; candidate: Candidate }) {
  const { t, i18n } = useTranslation();
  const card = useCard(candidate.print_id);
  const on = model.chosen === candidate.print_id;
  const backs = ocrBacks(model.scan, candidate);
  return (
    <button
      type="button"
      className="cand"
      aria-pressed={on}
      data-tile=""
      title={candidate.print_id}
      onClick={() => {
        model.setChosen(candidate.print_id);
      }}
    >
      <span className="thumb">
        <CardFace printId={candidate.print_id} name={card.data?.name} />
      </span>
      <span className="flex min-w-0 grow flex-col">
        <b className="t-sm truncate">{card.data?.name ?? candidate.print_id}</b>
        <span className="t-xs t-muted num truncate">
          {card.data ? printedNumber(card.data) : ""}
          {backs ? " · OCR ✓" : ""}
          {candidate.picked ? ` · ${t("review.picked")}` : ""}
        </span>
      </span>
      {candidate.picked ? null : (
        <span className="flex flex-col items-end gap-[3px]">
          <span className="num t-xs">
            {scoreText(candidate.score, i18n.resolvedLanguage ?? "de")}
          </span>
          <span className="score">
            <span
              className={`score-fill ${on ? "on" : ""}`}
              style={{ width: `${String(Math.round(candidate.score * 100))}%` }}
            />
          </span>
        </span>
      )}
    </button>
  );
}

/** A candidate on the wide screen: reference picture, name, key to press. */
function CandidateCard({
  model,
  candidate,
  slot,
}: {
  model: ReviewModel;
  candidate: Candidate;
  slot: number;
}) {
  const { t, i18n } = useTranslation();
  const card = useCard(candidate.print_id);
  const backs = ocrBacks(model.scan, candidate);
  return (
    <button
      type="button"
      className="cand-card"
      aria-pressed={model.chosen === candidate.print_id}
      data-tile=""
      title={candidate.print_id}
      onClick={() => {
        model.setChosen(candidate.print_id);
      }}
    >
      <span className="card-shape block">
        <CardFace printId={candidate.print_id} name={card.data?.name} quality="high" />
      </span>
      <span className="row-between">
        <b className="t-sm truncate">{card.data?.name ?? candidate.print_id}</b>
        {slot <= 9 ? <span className="kbd">{slot}</span> : null}
      </span>
      <span className="t-xs t-muted num">
        {card.data ? printedNumber(card.data) : ""}
        {candidate.picked
          ? ` · ${t("review.picked")}`
          : ` · ${scoreText(candidate.score, i18n.resolvedLanguage ?? "de")}`}
        {backs ? " · OCR ✓" : ""}
      </span>
    </button>
  );
}

/** Other prints of the chosen card: the right set is usually one of these. */
function Reprints({ model }: { model: ReviewModel }) {
  const { t } = useTranslation();
  const chosen = model.chosen;
  const reprints = useQuery({
    queryKey: ["reprints", chosen],
    queryFn: () => fetchReprints(chosen ?? ""),
    enabled: Boolean(chosen),
    staleTime: 10 * 60_000,
  });
  const exclude = model.candidates.map((candidate) => candidate.print_id);
  const shown = (reprints.data ?? []).filter((card) => !exclude.includes(card.id));
  if (!model.card.data || shown.length === 0) return null;
  return (
    <section className="flex flex-col gap-2">
      <span className="t-label">{t("review.reprints", { name: model.card.data.name })}</span>
      <ul className="flex gap-[10px] overflow-x-auto pb-1">
        {shown.map((card) => (
          <li key={card.id} className="w-20 shrink-0">
            <button
              type="button"
              className="tile w-full"
              data-tile=""
              title={card.id}
              aria-pressed={chosen === card.id}
              onClick={() => {
                model.setChosen(card.id);
              }}
            >
              <span className="card-shape block">
                <CardFace printId={card.id} name={card.name} lazy />
              </span>
              <span className="t-xs num truncate">{printedAs(card, card.id)}</span>
              <span className="t-xs t-muted truncate">{card.set_name}</span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

function ErrorLine({ model }: { model: ReviewModel }) {
  const { t } = useTranslation();
  const error = model.resolve.error ?? model.dismiss.error;
  if (!error || (error instanceof ApiError && error.code === "scan_already_resolved")) return null;
  return (
    <p role="alert" className="t-sm loss">
      {error instanceof ApiError
        ? t(`review.error.${error.code}`, { defaultValue: error.message })
        : t("review.error.generic")}
    </p>
  );
}

/** What a screen reader hears after every change: "Vorschlag 1 von 3, Duokles, …". */
function Announcement({ model }: { model: ReviewModel }) {
  const { t, i18n } = useTranslation();
  const at = model.candidates.findIndex((candidate) => candidate.print_id === model.chosen);
  const candidate = model.candidates[at];
  const card = model.card.data;
  if (!candidate || !card) return <p className="sr-only" aria-live="polite" />;
  const backs = ocrBacks(model.scan, candidate);
  return (
    <p className="sr-only" aria-live="polite">
      {[
        t("review.proposal", { current: at + 1, total: model.candidates.length }),
        card.name,
        printedNumber(card),
        candidate.picked
          ? t("review.picked")
          : `${t("review.score")} ${scoreText(candidate.score, i18n.resolvedLanguage ?? "de")}`,
        backs === null ? null : backs ? t("review.ocrMatches") : t("review.ocrDiffers"),
      ]
        .filter(Boolean)
        .join(", ")}
    </p>
  );
}

function CardSearch({ model }: { model: ReviewModel }) {
  const { t } = useTranslation();
  if (!model.searching) return null;
  return (
    <div className="flex flex-col gap-2">
      <CardPicker
        initialQuery={startingQuery(model.ocr)}
        onPick={(printId) => {
          model.setChosen(printId);
          model.setSearching(false);
        }}
      />
      <button
        type="button"
        className="btn btn-ghost btn-sm self-start"
        onClick={() => {
          model.setSearching(false);
        }}
      >
        {t("review.compare.closeSearch")}
      </button>
    </div>
  );
}

function ConfirmButton({ model, block = false }: { model: ReviewModel; block?: boolean }) {
  const { t } = useTranslation();
  const card = model.card.data;
  return (
    <button
      type="button"
      disabled={!model.canConfirm}
      onClick={model.confirm}
      className={`btn btn-primary ${block ? "btn-block" : ""}`}
    >
      <Check />
      {t(model.confirmKey)}
      {block && card ? ` · ${card.name} ${printedAs(card, "")}` : null}
      {block ? null : <span className="kbd border-current bg-transparent text-current">↵</span>}
    </button>
  );
}

/** The phone in comparison mode: photo beside the suggestion, the list, the chips, confirm. */
function CompareView({ model }: { model: ReviewModel }) {
  const { t, i18n } = useTranslation();
  return (
    <div className="flex flex-col gap-3">
      <div className="review-pair">
        <button
          type="button"
          className="flex flex-col gap-[6px]"
          aria-label={t("review.compare.open")}
          onClick={() => {
            model.setComparing(true);
          }}
        >
          <Photo model={model} />
          <span className="t-xs t-muted text-center">{t("review.yourPhoto")}</span>
        </button>
        <button
          type="button"
          className="flex flex-col gap-[6px]"
          aria-label={t("review.compare.open")}
          disabled={!model.chosen}
          onClick={() => {
            model.setComparing(true);
          }}
        >
          <span className="card-shape block">
            {model.chosen ? (
              <CardFace printId={model.chosen} name={model.card.data?.name} quality="high" />
            ) : null}
          </span>
          <span className="t-xs t-muted text-center">
            {model.chosen ? t("review.suggestion") : t("review.noCandidates")}
          </span>
        </button>
      </div>
      <NumberWarning scanId={model.scan.id} printId={model.chosen} onSwitch={model.setChosen} />
      <div className="flex flex-col gap-[6px]">
        {model.candidates.map((candidate) => (
          <CandidateRow key={candidate.print_id} model={model} candidate={candidate} />
        ))}
      </div>
      <CardSearch model={model} />
      <Reprints model={model} />
      <ResolveForm
        variants={model.variants}
        choice={model.choice}
        locale={i18n.resolvedLanguage ?? "de"}
        suggestedLanguage={model.scan.result?.language_suggestion}
        onChange={model.setChoice}
      />
      <div className="row gap-2">
        <button
          type="button"
          className="btn btn-secondary btn-sm grow"
          onClick={() => {
            model.setSearching(true);
          }}
        >
          <Search />
          {t("review.otherCard")}
        </button>
        <button
          type="button"
          className="btn btn-secondary btn-sm grow"
          disabled={model.busy}
          hidden={model.approving}
          onClick={() => {
            model.setEditing(true);
          }}
        >
          <Crop />
          {t("review.setCorners")}
        </button>
        <button
          type="button"
          className="icon-btn"
          aria-label={t(model.discardKey)}
          disabled={model.busy || model.settled}
          onClick={model.discard}
        >
          <X />
        </button>
      </div>
      <ConfirmButton model={model} block />
      <ErrorLine model={model} />
    </div>
  );
}

/** The wide screen: question, photo plus the top suggestions in a row, chips, keys. */
function DesktopView({ model }: { model: ReviewModel }) {
  const { t, i18n } = useTranslation();
  const result = model.scan.result;
  const ocr = model.ocr;
  const read = [readNumber(ocr), ocr?.set_code, result?.language_suggestion?.toUpperCase()]
    .filter(Boolean)
    .join(" · ");
  const calibration = (result?.reasons ?? []).includes("calibration_mode");
  return (
    <div className="flex flex-col gap-5">
      <div className="row-between items-start">
        <div className="flex flex-col gap-[2px]">
          <h2 className="t-h2">{t("review.question")}</h2>
          <span className="t-sm t-muted">
            {t("review.topPreselected")}
            {read ? (
              <>
                {" "}
                {t("review.ocrRead")} <span className="num">{read}</span>.
              </>
            ) : null}
          </span>
        </div>
        {calibration ? (
          <span className="badge badge-accent">
            <ShieldCheck />
            {t("review.calibration")}
          </span>
        ) : null}
      </div>
      <div className="grid grid-cols-4 items-start gap-5">
        <div className="flex flex-col gap-2">
          <button
            type="button"
            aria-label={t("review.compare.open")}
            onClick={() => {
              model.setComparing(true);
            }}
          >
            <Photo model={model} />
          </button>
          <div className="row gap-[6px]">
            <button
              type="button"
              className="btn btn-secondary btn-sm grow"
              disabled={model.busy}
              hidden={model.approving}
              onClick={() => {
                model.setEditing(true);
              }}
            >
              <Crop />
              {t("review.setCorners")} <span className="kbd">E</span>
            </button>
            <button
              type="button"
              className="icon-btn"
              aria-label={t("review.compare.open")}
              onClick={() => {
                model.setComparing(true);
              }}
            >
              <Maximize2 />
            </button>
          </div>
        </div>
        {model.candidates.slice(0, 3).map((candidate, index) => (
          <CandidateCard
            key={candidate.print_id}
            model={model}
            candidate={candidate}
            slot={index + 1}
          />
        ))}
      </div>
      <NumberWarning scanId={model.scan.id} printId={model.chosen} onSwitch={model.setChosen} />
      {model.candidates.length > 3 ? (
        <div className="flex flex-col gap-[6px]">
          <span className="t-label">{t("review.moreCandidates")}</span>
          <div className="grid grid-cols-2 gap-[6px] xl:grid-cols-3">
            {model.candidates.slice(3).map((candidate) => (
              <CandidateRow key={candidate.print_id} model={model} candidate={candidate} />
            ))}
          </div>
        </div>
      ) : null}
      <CardSearch model={model} />
      <ResolveForm
        variants={model.variants}
        choice={model.choice}
        locale={i18n.resolvedLanguage ?? "de"}
        suggestedLanguage={result?.language_suggestion}
        onChange={model.setChoice}
      />
      <div className="row flex-wrap gap-[10px]">
        <ConfirmButton model={model} />
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => {
            model.setSearching(true);
          }}
        >
          <Search />
          {t("review.otherCard")} <span className="kbd">S</span>
        </button>
        <button
          type="button"
          className="btn btn-ghost"
          disabled={model.busy || model.settled}
          onClick={model.discard}
        >
          <X />
          {t(model.discardKey)} <span className="kbd">D</span>
        </button>
      </div>
      <ErrorLine model={model} />
      <Reprints model={model} />
      <p className="t-xs t-muted">{t("review.keys")}</p>
    </div>
  );
}

export function ReviewCase({
  scan,
  mode = "compare",
  wide = false,
  approval,
  onDone,
}: {
  scan: Scan;
  mode?: ReviewMode;
  /** The desktop layout with key hints; phones get comparison or swipe. */
  wide?: boolean;
  /** Set when an administrator releases a tester's label instead of deciding a case. */
  approval?: Approval;
  onDone: (outcome: Outcome) => void;
}) {
  const { t } = useTranslation();
  const model = useReviewCase(scan, onDone, approval);

  let body;
  if (model.editing) {
    body = model.original ? (
      <CornerEditor
        photo={model.original}
        busy={model.recognise.isPending}
        onCancel={() => {
          model.setEditing(false);
        }}
        onSubmit={(quad, size) => {
          model.recognise.mutate({ quad, size });
        }}
      />
    ) : (
      <p className="t-muted">{t("common.loading")}</p>
    );
  } else if (wide) {
    body = <DesktopView model={model} />;
  } else if (mode === "swipe") {
    body = <SwipeDeck model={model} />;
  } else {
    body = <CompareView model={model} />;
  }

  return (
    <div data-testid="review-case" className="flex flex-col">
      <Announcement model={model} />
      {body}
      <CompareDialog
        open={model.comparing}
        onClose={() => {
          model.setComparing(false);
        }}
        photo={model.original ?? model.photo}
        candidates={model.candidates}
        chosen={model.chosen}
        onChoose={model.setChosen}
        variants={model.variants}
        choice={model.choice}
        onChoiceChange={model.setChoice}
        onConfirm={() => {
          model.confirm();
          model.setComparing(false);
        }}
        onDismiss={() => {
          model.discard();
          model.setComparing(false);
        }}
        busy={model.busy}
        ocr={model.ocr}
        notice={
          <NumberWarning scanId={scan.id} printId={model.chosen} onSwitch={model.setChosen} />
        }
      />
    </div>
  );
}
